/** SUNTRONIX ONE — Tenant portal: My Quotes.
 *  Shows RFQ requests (Sales Desk enquiries) awaiting a quote + issued STX
 *  quotes. Tenant can view the digital summary, download the PDF, accept, and
 *  upload their Customer PO. Tenant-scoped server-side. */
import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FileText, Loader2, Download, CheckCircle2, Upload, Clock, ScrollText, ShoppingBag,
} from "lucide-react";

const STATUS_TINT = {
  NEW: "bg-sky-50 text-sky-700", DRAFT: "bg-slate-100 text-slate-600",
  SENT: "bg-sky-50 text-sky-700", ACCEPTED: "bg-emerald-50 text-emerald-700",
  DECLINED: "bg-rose-50 text-rose-700", EXPIRED: "bg-amber-50 text-amber-700",
};
const money = (n, cur = "INR") => n == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(n).toLocaleString("en-IN")}`;

function AcceptDialog({ quote, onClose, onDone }) {
  const [name, setName] = useState("");
  const [po, setPo] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    setBusy(true);
    try {
      await api.post(`/tenant-catalogue/my-quotes/${quote.id}/accept`, { accepted_by_name: name, po_number: po });
      toast.success("Quotation accepted"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Accept failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="quote-accept-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" />Accept {quote?.code || quote?.quote_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-[12px] text-slate-600">Accepted by (name) *</label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="accept-name" className="h-9 mt-1" /></div>
          <div><label className="text-[12px] text-slate-600">PO number (optional)</label><Input value={po} onChange={(e) => setPo(e.target.value)} data-testid="accept-po" className="h-9 mt-1" /></div>
          <Button onClick={submit} disabled={busy} data-testid="accept-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Confirm acceptance
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MyQuotes() {
  const [data, setData] = useState({ enquiries: [], quotes: [] });
  const [loading, setLoading] = useState(true);
  const [accept, setAccept] = useState(null);
  const [uploading, setUploading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/tenant-catalogue/my-quotes"); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load quotes"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = (q) => window.open(`${API}/tenant-catalogue/my-quotes/${q.id}/pdf`, "_blank");

  const uploadPo = async (q, file) => {
    if (!file) return;
    setUploading(q.id);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/tenant-catalogue/my-quotes/${q.id}/po`, fd);
      toast.success("Purchase order uploaded"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(null); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5" data-testid="my-quotes-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Procurement</div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><ScrollText className="h-6 w-6 text-sky-600" />My Quotes</h1>
        <p className="text-sm text-slate-500">Your quotation requests and issued quotations.</p>
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
        <>
          <div className="space-y-2">
            <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber-500" />Quotation requests</div>
            {data.enquiries.length === 0 && <div className="text-[12px] text-slate-400">No open requests. Add products to your cart and Request a Quote.</div>}
            {data.enquiries.map((e) => (
              <div key={e.id} data-testid={`enquiry-${e.id}`} className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3">
                <ShoppingBag className="h-4 w-4 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{e.enquiry_no} <span className="text-[11px] text-slate-400">· {e.product_codes?.length || 0} item(s)</span></div>
                  <div className="text-[12px] text-slate-500">{e.delivery_location || "—"} · {new Date(e.created_at).toLocaleDateString()}</div>
                </div>
                <Badge className={STATUS_TINT[e.status] || "bg-slate-100 text-slate-600"}>{e.status === "NEW" ? "Awaiting quote" : e.status}</Badge>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5"><FileText className="h-4 w-4 text-sky-600" />Quotations</div>
            {data.quotes.length === 0 && <div className="text-[12px] text-slate-400">No quotations issued yet.</div>}
            {data.quotes.map((q) => (
              <div key={q.id} data-testid={`quote-${q.id}`} className="rounded-lg border border-slate-200 px-4 py-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{q.code || q.quote_number} {q.subject && <span className="text-[11px] text-slate-400">· {q.subject}</span>}</div>
                  <div className="text-[12px] text-slate-500">{money(q.grand_total_inr ?? q.grand_total)} · valid till {q.valid_until || "—"}{q.customer_accept_po_number ? ` · PO ${q.customer_accept_po_number}` : ""}</div>
                </div>
                <Badge className={STATUS_TINT[q.status] || "bg-slate-100 text-slate-600"}>{q.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(q)} data-testid={`quote-pdf-${q.id}`}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
                {q.status !== "ACCEPTED" && <Button size="sm" onClick={() => setAccept(q)} data-testid={`quote-accept-${q.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Accept</Button>}
                <label className="cursor-pointer inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-slate-200 text-[12px] text-slate-700 hover:bg-slate-50" data-testid={`quote-po-${q.id}`}>
                  {uploading === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}PO
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(ev) => { uploadPo(q, ev.target.files?.[0]); ev.target.value = ""; }} />
                </label>
              </div>
            ))}
          </div>
        </>
      )}
      {accept && <AcceptDialog quote={accept} onClose={() => setAccept(null)} onDone={() => { setAccept(null); load(); }} />}
    </div>
  );
}
